import React, { useState, useEffect, useCallback } from "react";
import { getActions, createAction, updateAction, deleteAction, getNews, createNews, updateNews, deleteNews, uploadFile, getUploadUrl } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Plus, Pencil, Trash2, Save, Upload, Calendar, User, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const emptyItem = {
  title: "",
  excerpt: "",
  content: "",
  image: "",
  date: new Date().toISOString().split("T")[0],
  author: "",
};

export const AdminBlogManager = ({ type, label }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const isAct = type === "actions";

  const loadItems = useCallback(async () => {
    try {
      const data = isAct ? await getActions() : await getNews();
      setItems(data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [isAct]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const onNew = () => { setEditing({ ...emptyItem }); setIsNew(true); };
  const onEdit = (item) => { setEditing({ ...item }); setIsNew(false); };
  const onCancel = () => setEditing(null);

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    setDeleting(id);
    try {
      isAct ? await deleteAction(id) : await deleteNews(id);
      setItems((p) => p.filter((a) => a.id !== id));
      toast.success("Supprimé");
    } catch (err) { toast.error("Erreur"); }
    finally { setDeleting(null); }
  };

  const onSave = async () => {
    if (!editing.title || !editing.content) { toast.error("Titre et contenu obligatoires"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const fn = isAct ? createAction : createNews;
        const created = await fn(editing);
        setItems((p) => [created, ...p]);
        toast.success("Article créé");
      } else {
        const fn = isAct ? updateAction : updateNews;
        const updated = await fn(editing.id, editing);
        setItems((p) => p.map((a) => (a.id === updated.id ? updated : a)));
        toast.success("Article mis à jour");
      }
      setEditing(null);
    } catch (err) { toast.error("Erreur sauvegarde"); }
    finally { setSaving(false); }
  };

  const onImgUpload = async (file) => {
    try {
      const res = await uploadFile(file);
      setEditing((p) => ({ ...p, image: getUploadUrl(res.url) }));
      toast.success("Image uploadée");
    } catch (err) { toast.error("Erreur upload"); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i=><Skeleton key={i} className="h-24 w-full"/>)}</div>;

  if (editing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-sky-600 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <Separator orientation="vertical" className="h-5" />
          <h2 className="text-lg font-semibold text-slate-800">{isNew ? "Nouvel article" : "Modifier"}</h2>
        </div>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 space-y-5">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Titre *</Label>
              <Input value={editing.title} onChange={(e) => setEditing(p => ({...p, title: e.target.value}))} placeholder="Titre" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Résumé</Label>
              <Textarea value={editing.excerpt} onChange={(e) => setEditing(p => ({...p, excerpt: e.target.value}))} rows={2} className="resize-y text-sm" placeholder="Court résumé..." />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Contenu *</Label>
              <Textarea value={editing.content} onChange={(e) => setEditing(p => ({...p, content: e.target.value}))} rows={10} className="resize-y text-sm" placeholder="Contenu complet..." />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1.5 block">Date</Label>
                <Input type="date" value={editing.date} onChange={(e) => setEditing(p => ({...p, date: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1.5 block">Auteur</Label>
                <Input value={editing.author} onChange={(e) => setEditing(p => ({...p, author: e.target.value}))} placeholder="Auteur" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Image</Label>
              {editing.image && <img src={editing.image} alt="Preview" className="w-full h-40 object-cover rounded-lg mb-3" />}
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-200 cursor-pointer">
                  <Upload className="w-4 h-4" /> Uploader
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) onImgUpload(e.target.files[0]); }} />
                </label>
                <Input value={editing.image} onChange={(e) => setEditing(p => ({...p, image: e.target.value}))} placeholder="ou URL" className="flex-1 text-sm" />
              </div>
            </div>
            <Separator />
            <div className="flex gap-3">
              <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-500 text-white text-sm font-semibold rounded-lg hover:bg-sky-600 disabled:opacity-50">
                <Save className="w-4 h-4" /> {saving ? "Sauvegarde..." : "Sauvegarder"}
              </button>
              <button onClick={onCancel} className="px-5 py-2.5 text-slate-600 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">Annuler</button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">{label} ({items.length})</h2>
        <button onClick={onNew} className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600">
          <Plus className="w-4 h-4" /> Nouvel article
        </button>
      </div>
      {items.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center"><p className="text-slate-500">Aucun article.</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card key={item.id} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {item.image && <img src={item.image} alt="" className="w-20 h-14 rounded-lg object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-slate-800 truncate">{item.title}</h3>
                    <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(item.date).toLocaleDateString("fr-FR")}</span>
                      {item.author && <span className="flex items-center gap-1"><User className="w-3 h-3" />{item.author}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => onEdit(item)} className="p-2 text-slate-400 hover:text-sky-500 rounded-lg hover:bg-sky-50"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(item.id)} disabled={deleting===item.id} className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 disabled:opacity-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
